import './instrument';

import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';

import App from './app/app';
import flagArgentina from './assets/flags/flag-argentina.png';
import flagBrazil from './assets/flags/flag-brazil.png';
import homeDashboardBonusGift from './assets/images/home-dashboard-bonus-gift.png';
import homeDashboardReferralBackground from './assets/images/home-dashboard-referral-background.png';
import profileAvatarDefault from './assets/images/profile-avatar-default.png';
import raffleProfessionalHeroBike from './assets/images/raffle/raffle-professional-hero-bike.jpg';
import raffleProfessionalNetwork from './assets/images/raffle/raffle-professional-network.jpg';
import raffleProfessionalReferralProgressBackground from './assets/images/raffle/raffle-professional-referral-progress-background.png';
import raffleProfessionalRule1 from './assets/images/raffle/raffle-professional-rule-1.png';
import raffleProfessionalRule2 from './assets/images/raffle/raffle-professional-rule-2.png';
import raffleProfessionalRule3 from './assets/images/raffle/raffle-professional-rule-3.png';
import raffleProfessionalRule4 from './assets/images/raffle/raffle-professional-rule-4.png';
import raffleProfessionalRule5 from './assets/images/raffle/raffle-professional-rule-5.png';
import referralBonusHeroBackground from './assets/images/referral-bonus-hero-background.png';
import referralBonusTicket from './assets/images/referral-bonus-ticket.png';
import registrationCompleteAvatarPhoto from './assets/images/registration-complete-avatar-photo.png';
import registrationGoalHero from './assets/images/registration-goal-hero.png';
import registrationGoalValueHero from './assets/images/registration-goal-value-hero.png';
import registrationPhotoSelfieGuide from './assets/images/registration-photo-selfie-guide.png';
import registrationPhotoSoloGuide from './assets/images/registration-photo-solo-guide.png';
import registrationProfileBenefitsHero from './assets/images/registration-profile-benefits-hero.png';
import registrationProfileCostsHero from './assets/images/registration-profile-costs-hero.png';
import registrationProfileHero from './assets/images/registration-profile-hero.png';
import tipCheckoutPromoBackground from './assets/images/tip-checkout-promo-background.png';
import tipRewardStepGiftInner from './assets/images/tip-reward-step-gift-inner.png';
import paymentTetherLogo from './assets/logos/logo-payment-tether.png';
import withdrawalPaymentPixLogo from './assets/logos/logo-withdrawal-payment-pix.png';
import './i18n';
import { fitDesktopPwaWindow } from './shared/config';

const BOOTSTRAP_IMAGE_TIMEOUT_MS = 2500;
const CRITICAL_IMAGE_SOURCES = [
  flagBrazil,
  flagArgentina,
  registrationGoalHero,
  registrationGoalValueHero,
  registrationPhotoSelfieGuide,
  registrationPhotoSoloGuide,
  registrationProfileHero,
  registrationProfileCostsHero,
  registrationProfileBenefitsHero,
  homeDashboardBonusGift,
  profileAvatarDefault,
  homeDashboardReferralBackground,
  referralBonusHeroBackground,
  referralBonusTicket,
  raffleProfessionalHeroBike,
  raffleProfessionalNetwork,
  raffleProfessionalReferralProgressBackground,
  raffleProfessionalRule1,
  raffleProfessionalRule2,
  raffleProfessionalRule3,
  raffleProfessionalRule4,
  raffleProfessionalRule5,
  registrationCompleteAvatarPhoto,
  tipRewardStepGiftInner,
  tipCheckoutPromoBackground,
  paymentTetherLogo,
  withdrawalPaymentPixLogo,
] as const;

function preloadImage(src: string): Promise<void> {
  const preloadLink = document.createElement('link');
  preloadLink.rel = 'preload';
  preloadLink.as = 'image';
  preloadLink.href = src;
  document.head.appendChild(preloadLink);

  return new Promise(resolve => {
    const image = new Image();
    let isSettled = false;

    const settle = () => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      resolve();
    };

    image.onload = settle;
    image.onerror = settle;
    image.src = src;

    if (typeof image.decode === 'function') {
      void image
        .decode()
        .then(settle)
        .catch(() => undefined);
    }
  });
}

async function preloadCriticalImages(): Promise<void> {
  await Promise.all(CRITICAL_IMAGE_SOURCES.map(source => preloadImage(source)));
}

async function bootstrapApp() {
  await Promise.race([
    preloadCriticalImages(),
    new Promise<void>(resolve => {
      window.setTimeout(resolve, BOOTSTRAP_IMAGE_TIMEOUT_MS);
    }),
  ]);

  fitDesktopPwaWindow();

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary>
        <App />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );

  if (import.meta.env.PROD) {
    registerSW({ immediate: true });
  }
}

void bootstrapApp();
